import React, { useEffect, useRef, useState } from "react";
import { columnsType, Task } from "../../types/public-types";
import "./index.css";
import { GanttTaskExtendedType } from "@/components/views/custom";

export type TaskListProps = {
  columns: columnsType[];
  headerHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  rowHeight: number;
  ganttHeight: number;
  scrollY: number;
  locale: string;
  tasks: Task[];
  taskListRef: React.RefObject<HTMLDivElement>;
  horizontalContainerClass?: string;
  selectedTask: Task | undefined;
  setSelectedTask: (task: string) => void;
  onExpanderClick: (task: Task) => void;
  taskSelect: (task: Task) => void;
  taskDblClick?: (task: Task) => void;
  taskListWidth: number;
  setCurrentColumns?: (columns: columnsType[]) => void;
  setSortedTask?: (tasks: Task[]) => void;
  suppressTaskListExpander?: boolean;
  bulkMultiSelectActive?: boolean;
  TaskListHeader: React.FC<{
    headerHeight: number;
    rowWidth: string;
    fontFamily: string;
    fontSize: string;
    columns: columnsType[];
    setColumns?: (columns: columnsType[]) => void;
    onSort: (key: string) => void;
    bulkMultiSelectActive?: boolean;
  }>;
  TaskListTable: React.FC<{
    rowHeight: number;
    rowWidth: string;
    fontFamily: string;
    fontSize: string;
    locale: string;
    tasks: Task[];
    selectedTaskId: string;
    selectedTask: Task | undefined;
    columns: columnsType[];
    setSelectedTask: (taskId: string) => void;
    onExpanderClick: (task: Task) => void;
    taskSelect: (task: Task) => void;
    taskDblClick?: (task: Task) => void;
    setSortedTask?: (task: Task[]) => void;
    scrollY?: number;
    ganttHeight?: number;
    suppressTaskListExpander?: boolean;
    bulkMultiSelectActive?: boolean;
  }>;
};

export const TaskList: React.FC<TaskListProps> = ({
  headerHeight,
  fontFamily,
  fontSize,
  rowWidth,
  rowHeight,
  scrollY,
  tasks,
  selectedTask,
  setSelectedTask,
  onExpanderClick,
  taskSelect,
  locale,
  taskListRef,
  horizontalContainerClass,
  TaskListHeader,
  TaskListTable,
  taskListWidth,
  columns,
  taskDblClick,
  ganttHeight,
  setCurrentColumns,
  setSortedTask,
  suppressTaskListExpander,
  bulkMultiSelectActive,
}) => {
  // const [currentColumns, setCurrentColumns] = useState(columns);
  const [dataSource, setDataSource] = useState<Task[]>(tasks);
  const [ascendingToggle, setAscendingToggle] = useState<boolean>(false);
  const [sortOn, setSortOn] = useState<string>("start");
  const horizontalContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.shiftKey || e.deltaX) {
        const deltaFactor = e.deltaMode === 1 ? 30 : e.deltaMode === 2 ? el.clientWidth : 1;
        const delta = (e.deltaX || e.deltaY) * deltaFactor;
        el.scrollLeft += delta;
        e.preventDefault();
        e.stopPropagation();
      }
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, []);

  useEffect(() => {
    if (horizontalContainerRef.current) {
      horizontalContainerRef.current.scrollTop = scrollY;
    }
  }, [scrollY]);
  useEffect(() => {
    if (tasks) {
      setDataSource(tasks);
    }
  }, [tasks]);

  function isValidDate(dateString: string) {
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    return regex.test(dateString);
  }

  function parseDate(dateString: string) {
    const [day, month, year] = dateString.split("/").map(Number);
    return [day, month, year];
  }const sorting = (key: string, type?: string) => {
  // Rebuild proper hierarchy from flat dataSource
  const hierarchy = buildHierarchy(dataSource as GanttTaskExtendedType[]);

  // Sort hierarchy at all levels
  const sortedHierarchy = sortHierarchy(hierarchy, key as any, type);

  // Flatten sorted hierarchy for rendering
  const flattened = flattenTasks(sortedHierarchy, true);

  setSortedTask?.(flattened);
  setDataSource(flattened);
  setSortOn(key);

  if (type) {
    setAscendingToggle(type !== "asc");
  } else {
    setAscendingToggle((prev) => !prev);
  }
};

// Build hierarchy from flat list
function buildHierarchy(flatTasks: GanttTaskExtendedType[]): GanttTaskExtendedType[] {
  const taskMap = new Map<string, GanttTaskExtendedType>();

  // Clone all tasks into map
  flatTasks.forEach(task => {
    taskMap.set(task.id, { ...task, subtask: [] });
  });

  const roots: GanttTaskExtendedType[] = [];

  taskMap.forEach(task => {
    if (task.project && taskMap.has(task.project)) {
      taskMap.get(task.project)!.subtask!.push(task);
    } else {
      roots.push(task);
    }
  });

  return roots;
}

// Recursively sort hierarchy
function sortHierarchy(tasks: GanttTaskExtendedType[], key: keyof Task, type?: string):any {
  return [...tasks]
    .sort(dynamicSort(key, type))
    .map((task:any) => ({
      ...task,
      subtask: task.subtask ? sortHierarchy(task.subtask, key, type) : []
    }));
}

// Flatten hierarchy
function flattenTasks(tasks: GanttTaskExtendedType[], hidden: boolean, levelPrefix = ""): GanttTaskExtendedType[] {
  const result: GanttTaskExtendedType[] = [];
  tasks.forEach((task, index) => {
    const level = levelPrefix ? `${levelPrefix}.${index + 1}` : `${index + 1}`;
    result.push({ ...task, level });

    if (task.subtask && (!hidden || !task.hideChildren)) {
      result.push(...flattenTasks(task.subtask, hidden, level));
    }
  });
  return result;
}

function dynamicSort(property: keyof Task, type?: string) {
  return function (a: Task, b: Task) {
    let aValue: any = a[property];
    let bValue: any = b[property];

    if (typeof aValue === "string" && !isValidDate(aValue))
      aValue = aValue.toUpperCase();
    if (typeof bValue === "string" && !isValidDate(bValue))
      bValue = bValue.toUpperCase();

    if (isValidDate(aValue) && isValidDate(bValue)) {
      const [aDay, aMonth, aYear] = parseDate(aValue);
      const [bDay, bMonth, bYear] = parseDate(bValue);
      aValue = new Date(aYear, aMonth - 1, aDay);
      bValue = new Date(bYear, bMonth - 1, bDay);
    }

    if (!isNaN(Number(aValue))) aValue = Number(aValue);
    if (!isNaN(Number(bValue))) bValue = Number(bValue);

    let comparison = 0;
    if (aValue > bValue) comparison = 1;
    else if (aValue < bValue) comparison = -1;

    if (type) {
      return type === "asc" ? comparison : -comparison;
    }
    return ascendingToggle ? comparison : -comparison;
  };
}


  const headerProps = {
    headerHeight,
    fontFamily,
    fontSize,
    rowWidth,
    columns: columns,
    setColumns: setCurrentColumns,
    onSort: sorting,
    sortField: sortOn,
    sortOrder: ascendingToggle ? "asc" : "desc",
    bulkMultiSelectActive,
  };

  const tableProps = {
    columns: columns,
    rowHeight,
    rowWidth,
    fontFamily,
    fontSize,
    tasks: dataSource,
    locale,
    selectedTask,
    selectedTaskId: selectedTask?.id ?? "",
    setSelectedTask,
    onExpanderClick,
    taskSelect,
    taskDblClick,
    scrollY,
    ganttHeight,
    suppressTaskListExpander,
    bulkMultiSelectActive,
  };

  return (
    <div
      ref={taskListRef}
      className="max-w-fit rmResizeIcon "
      style={{
        width: `${taskListWidth}px`,
        minWidth: `${taskListWidth}px`,
        resize: "horizontal",
        overflow: "hidden",
        position: "relative",
        borderRight: "2px solid #80808011",
      }}
    >
      <div ref={scrollContainerRef} data-tasklist-scroll-container style={{ overflowX: "auto", height: "100%" }}>
        <div style={{ width: "max-content" }}>
          <TaskListHeader {...headerProps} />
          <div
            ref={horizontalContainerRef}
            className={`${horizontalContainerClass ?? ""} scrollRm`}
            style={ganttHeight ? { height: ganttHeight } : {}}
          >
            <TaskListTable {...tableProps} />
          </div>
        </div>
      </div>
    </div>
  );
};
